import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Typography, Paper, Grid, Button, Divider, FormControlLabel, Checkbox, Box, Snackbar, Alert, FormControl, InputLabel, Select, MenuItem, InputAdornment } from '@mui/material';
import StadiumIcon from '@mui/icons-material/Stadium';
import ChildFriendlyIcon from '@mui/icons-material/ChildFriendly';
import AccessibilityIcon from '@mui/icons-material/Accessibility';
import AccessibleForwardIcon from '@mui/icons-material/AccessibleForward';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import theme from '../components/Theme';
import { LoadingButton } from '@mui/lab';
import { useAuth } from '../contexts/AuthContext';
import { usePlaceForm } from '../hooks/usePlaceForm';
import { PlaceFormFields } from '../components/PlaceFormFields';
import { CoordinateInput } from '../components/CoordinateInput';
import { AddressFields } from '../components/AddressFields';
import { CategoryFields } from '../components/CategoryFields';
import { FeeFields } from '../components/FeeFields';
import { EquipmentFields } from '../components/EquipmentFields';
import { ContactFields } from '../components/ContactFields';
import { ImageUpload } from '../components/ImageUpload';

const CreatePlace = () => {
  const { isAuthenticated, userRole } = useAuth();
  const navigate = useNavigate();
  const {
    formData,
    errors,
    handleChange,
    handleCoordinateChange,
    handleMapClick,
    handleSubmit,
    isSubmitting,
    isSuccess,
    submitError,
  } = usePlaceForm();

  const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false);
  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);

  // Handle success
  useEffect(() => {
    if (isSuccess) {
      setSuccessSnackbarOpen(true);
      // Navigate after delay to allow user to see message
      const timer = setTimeout(() => {
        navigate('/places');
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [isSuccess, navigate]);

  // Handle error
  useEffect(() => {
    if (submitError) {
      setErrorSnackbarOpen(true);
    }
  }, [submitError]);

  // Extract error message
  const errorMessage = submitError?.response?.data?.message 
    || submitError?.message 
    || 'Error submitting place. Please try again.';

  if (!isAuthenticated) {
    return <Navigate to="/" />;
  }

  return (
    <Grid container justifyContent="center" sx={{ px: 1 }}>
      <Grid container item xs={12} md={12} lg={12} sx={{ px: 20, pb: 10 }}>
        <Grid item xs={12} md={12} lg={12} sx={{ pt: 2, pb: 2 }}>
          <Typography variant="h4">
            {userRole === 'admin' ? 'Create Place' : 'Suggest Place'}
          </Typography>
        </Grid>

        {/* Main Information Paper (left column) */}
        <Grid item xs={5} md={5} lg={5} sx={{ pt: 2 }}>
          <Paper elevation={3} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Main Information
            </Typography>
            <PlaceFormFields formData={formData} errors={errors} onChange={handleChange} />
            
            {/* Contact Information (inside Main Information) */}
            <ContactFields formData={formData} errors={errors} onChange={handleChange} />
            
            {/* Type field (with icon) */}
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <StadiumIcon sx={{ color: theme.palette.accent.main }} />
                Type <span style={{ color: 'red' }}>*</span>
              </Typography>
            </Box>
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.type}
                onChange={(e) => handleChange('type', e.target.value)}
                label="Type"
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    '&:hover': { borderColor: 'primary.main' },
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: 2,
                    borderColor: 'primary.main',
                  },
                }}
              >
                <MenuItem value="Outdoor">Outdoor</MenuItem>
                <MenuItem value="Indoor">Indoor</MenuItem>
              </Select>
            </FormControl>
            
            {/* Fee Fields (with icons) */}
            <FeeFields formData={formData} onChange={handleChange} />
            
            {/* Category Fields (Age Category, Tags) with icons */}
            <CategoryFields formData={formData} onChange={handleChange} />
            
            {/* Kid Friendly and Disability Friendly (with icons) */}
            <Grid container sx={{ mt: 2 }}>
              <Grid item xs={6} md={6} lg={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="kid_friendly"
                      checked={formData.kid_friendly}
                      onChange={(e) => handleChange('kid_friendly', e.target.checked)}
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      <ChildFriendlyIcon sx={{ color: theme.palette.accent.main }} />
                      Kid Friendly
                    </Box>
                  }
                />
              </Grid>
              <Grid item xs={6} md={6} lg={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="disability_friendly"
                      checked={formData.disability_friendly}
                      onChange={(e) => handleChange('disability_friendly', e.target.checked)}
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      <AccessibleForwardIcon sx={{ color: theme.palette.accent.main }} />
                      Disability Friendly
                    </Box>
                  }
                />
              </Grid>
            </Grid>
            
            {/* Equipment Details (with icon) */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                <FitnessCenterIcon sx={{ color: theme.palette.accent.main, verticalAlign: 'middle', mr: 1 }} />
                Equipment Details
              </Typography>
              <EquipmentFields formData={formData} onChange={handleChange} />
            </Box>
          </Paper>
        </Grid>

        {/* Spacer */}
        <Grid item xs={1} md={1} lg={1}></Grid>

        {/* Address Information Paper (right column) */}
        <Grid item xs={6} md={6} lg={6} sx={{ pt: 2 }}>
          <Paper elevation={3} sx={{ p: 2 }}>
            <CoordinateInput
              coordinates={formData.coordinates}
              onChange={handleCoordinateChange}
              onMapClick={handleMapClick}
            />
            <AddressFields formData={formData} errors={errors} onChange={handleChange} />
            
            {/* Images Section (inside Address Information Paper) */}
            <Box sx={{ mt: 3 }}>
              <ImageUpload images={formData.images} onChange={(images) => handleChange('images', images)} />
            </Box>
          </Paper>
        </Grid>

        {/* Submit Button (centered at bottom) */}
        <Grid item xs={12} md={12} lg={12} sx={{ pt: 4, pb: 4, display: 'flex', justifyContent: 'center' }}>
          <LoadingButton
            variant="contained"
            color="primary"
            size="large"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={isSubmitting}
            sx={{ minWidth: '200px', minHeight: '44px', transition: 'all 0.2s ease-in-out' }}
          >
            Submit
          </LoadingButton>
        </Grid>
      </Grid>

      {/* Success Snackbar */}
      <Snackbar
        open={successSnackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSuccessSnackbarOpen(false)}
      >
        <Alert severity="success" onClose={() => setSuccessSnackbarOpen(false)}>
          Place suggestion is received. Admin will review and decide if place can be published
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={errorSnackbarOpen}
        autoHideDuration={6000}
        onClose={() => setErrorSnackbarOpen(false)}
      >
        <Alert severity="error" onClose={() => setErrorSnackbarOpen(false)}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default CreatePlace;
